/**
 * Card-lookup sub-modal. Reads the card form's Card Name field (or an
 * explicit refine string), searches OUR OWN catalog (catalog_products,
 * synced nightly from TCGCSV — all games incl. Pokemon since 2026-07-13),
 * presents a grid of matches with images + market price, and on pick
 * auto-fills the underlying Card edit form.
 *
 * The external Pokemon TCG API grid remains ONLY for full API-ID searches
 * ("swsh12tg-TG05" → set.id:swsh12tg number:TG05), which the catalog can't
 * resolve. Everything else — name, name+number, name+set — is one fast
 * Supabase query instead of the slow external API.
 *
 * Mounts into #pickerRoot so it stacks above the Card edit modal.
 */
import { escapeHtml, toast } from "../lib/dom";
import { normalizeSetName } from "../lib/setNames";
import { fmt$ } from "../utils/format";
import { Settings } from "../lib/prefs";
import { markModalDirty } from "./modal";
import { resolveCatalogProduct, searchCatalogCards, cleanGroupName, cleanCardName, GAME_TO_CATEGORY, type CatalogSearchRow } from "../lib/catalogResolve";
import { trendButtonHtml, toggleCatalogTrend, isTrendClick } from "./catalogTrend";
import { syncVariantSelect } from "./variantSelect";
import { loadConditionMultipliers, multiplierFor, conditionSourceSuffix } from "../lib/conditionMultiplier";

/**
 * A pick writes a CATALOG price, which is a Near Mint price. If the lot is a
 * played card, write the condition-adjusted number instead — otherwise the form
 * shows one value, the nightly job writes another, and picking a card silently
 * undoes the adjustment on an already-priced lot (which is exactly what
 * happened to 17 lots on 2026-09-05).
 *
 * Falls back to the raw price when multipliers aren't cached yet: data entry
 * must not block on a config fetch, and revalue_inventory() corrects it that
 * night. The cache is warmed when the lookup opens.
 */
function applyFormCondition(
  form: HTMLFormElement, unit: number, baseSource: string,
): { unit: number; source: string } {
  const grade = form.querySelector<HTMLSelectElement>('[name="Condition/Grade"]')?.value || "";
  const mult = multiplierFor(grade);
  if (mult == null || mult === 1) return { unit: +unit.toFixed(2), source: baseSource };
  return {
    unit: +(unit * mult).toFixed(2),
    source: baseSource + conditionSourceSuffix(grade, mult),
  };
}

const PAGE_SIZE = 50;

interface ApiCard {
  id: string;
  name: string;
  number: string;
  set?: { name?: string };
  images?: { small?: string; large?: string };
  tcgplayer?: { prices?: Record<string, { market?: number }> };
}

let _quickCards: ApiCard[] = [];

function renderSubModal(title: string, body: string, footer: string, wide = true): void {
  let root = document.getElementById("pickerRoot");
  if (!root) { root = document.createElement("div"); root.id = "pickerRoot"; document.body.appendChild(root); }
  root.innerHTML = `
    <div class="fixed inset-0 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto" style="z-index:55">
      <div class="panel w-full ${wide ? "max-w-5xl" : "max-w-xl"} my-6 shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <h3 class="font-semibold">${escapeHtml(title)}</h3>
          <button class="btn btn-ghost !py-1 !px-2" id="_qclClose" title="Back to form">✕</button>
        </div>
        <div class="p-4">${body}</div>
        ${footer ? `<div class="p-4 border-t border-[var(--border)] flex justify-end gap-2">${footer}</div>` : ""}
      </div>
    </div>`;
  document.getElementById("_qclClose")?.addEventListener("click", closeQuickLookup);
}

export function closeQuickLookup(): void {
  document.getElementById("pickerRoot") && (document.getElementById("pickerRoot")!.innerHTML = "");
}

function showLoading(searchTerm: string, page: number): void {
  const refineEsc = escapeHtml(searchTerm).replace(/'/g, "&#39;");
  const body = `
    <div class="space-y-3 mb-3">
      <div class="flex flex-wrap gap-2">
        <input type="text" id="quickLookupRefine" class="input flex-1 min-w-[240px]"
               placeholder='Refine: e.g. "umbreon 161" or "charizard ex"'
               value="${refineEsc}" disabled>
        <button class="btn btn-primary" disabled>Searching…</button>
      </div>
    </div>
    <div class="py-16 text-center">
      <div class="inline-flex items-center gap-3 text-[var(--muted)]">
        <div class="w-6 h-6 border-2 rounded-full animate-spin" style="border-color:#6366f1; border-top-color:transparent"></div>
        <div>Searching${page > 1 ? ` page ${page}` : ""} for <code>${escapeHtml(searchTerm)}</code>…</div>
      </div>
    </div>`;
  renderSubModal("Pick a match — searching…", body, `<button class="btn btn-ghost" id="_qclCancel">Cancel</button>`);
  document.getElementById("_qclCancel")?.addEventListener("click", closeQuickLookup);
}

function showError(searchTerm: string, err: any): void {
  const refineEsc = escapeHtml(searchTerm).replace(/'/g, "&#39;");
  const body = `
    <div class="space-y-3 mb-3">
      <div class="flex flex-wrap gap-2">
        <input type="text" id="quickLookupRefine" class="input flex-1 min-w-[240px]"
               placeholder='Refine: e.g. "umbreon 161"'
               value="${refineEsc}">
        <button class="btn btn-primary" id="_qclRetry">Retry</button>
      </div>
    </div>
    <div class="panel-2 p-4 text-rose-300 text-sm">Lookup failed: ${escapeHtml(err?.message || String(err))}</div>`;
  renderSubModal("Pick a match — error", body, `<button class="btn btn-ghost" id="_qclCancel2">Cancel</button>`);
  document.getElementById("_qclCancel2")?.addEventListener("click", closeQuickLookup);
  document.getElementById("_qclRetry")?.addEventListener("click", () => {
    const v = (document.getElementById("quickLookupRefine") as HTMLInputElement).value;
    quickCardLookup(1, v);
  });
  document.getElementById("quickLookupRefine")?.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") {
      e.preventDefault();
      const v = (document.getElementById("quickLookupRefine") as HTMLInputElement).value;
      quickCardLookup(1, v);
    }
  });
  setTimeout(() => {
    const ri = document.getElementById("quickLookupRefine") as HTMLInputElement | null;
    if (ri) { ri.focus(); ri.setSelectionRange(ri.value.length, ri.value.length); }
  }, 50);
}

/** Optional context that narrows the displayed results without making the
 *  server-side query so strict that it returns zero. number is matched
 *  client-side against each result's card.number; set is added as a server-
 *  side filter (overriding any card-form Set input when supplied). */
export interface LookupNarrow {
  number?: string;
  set?: string;
  /** Game of the CALLING row/form (inline purchase/trade/txn/rip pickers).
   *  Overrides the card-form Game select — without it, inline callers were
   *  silently searching the Pokemon catalog for every game (bug, 2026-07-18). */
  game?: string;
}

export function quickCardLookup(
  pageInput?: number,
  customSearchTerm?: string,
  narrowIn?: LookupNarrow,
): void {
  // narrow is reassigned below when defaulted from the card form, so it
  // can't be a const param.
  let narrow: LookupNarrow | undefined = narrowIn;
  const page = Math.max(1, pageInput || 1);
  const cardForm = document.getElementById("cardForm") as HTMLFormElement | null;

  // Warm the condition-multiplier cache so applyFormCondition() has it by the
  // time the user picks a card (fire-and-forget; a miss just means no discount
  // until the nightly job runs).
  void loadConditionMultipliers();

  const searchTerm = (typeof customSearchTerm === "string"
    ? customSearchTerm
    : (cardForm ? ((cardForm.querySelector('[name="Card Name"]') as HTMLInputElement)?.value || "") : "")
  ).trim();
  // Default-narrow: when called from the main Card form with no explicit
  // narrow, pull Card # off the form so the results still get filtered.
  if (!narrow && cardForm) {
    const cardNum = ((cardForm.querySelector('[name="Card #"]') as HTMLInputElement)?.value || "").trim();
    if (cardNum) narrow = { number: cardNum };
  }
  if (!searchTerm) { toast("Type a card name (or 'name 123') first", "err"); return; }

  const cleaned = searchTerm.replace(/"/g, "").trim();
  // Full Pokemon API ID format ("swsh12tg-TG05") — only the API can search
  // set.id, so that one input shape stays on the API path below.
  //
  // The NUMBER half must contain a digit. Without that guard this matched any
  // hyphenated word-pair, so every hyphenated Pokemon NAME was routed to the
  // API as a set-id query: "ho-oh" became `set.id:ho number:oh` -> 0 results
  // (and, when the API was having a bad day, an HTTP 500 toast instead).
  // Ho-Oh, Porygon-Z, Jangmo-o, Kommo-o, Chien-Pao, Wo-Chien, Ting-Lu and
  // friends were all unsearchable from the purchase/trade row pickers.
  // Set ids without digits are real ("basep-1"), so only the right half is
  // constrained. Names now fall through to the catalog path, which doesn't
  // depend on the external API at all.
  const idMatch = cleaned.match(/^([a-z0-9]+)-([a-z0-9]*\d[a-z0-9]*)$/i);

  // Optional set filter. Prefer the explicit narrow.set (passed by inline
  // forms that don't have a card form) over the card-form Set combobox.
  const setInput = cardForm
    ? cardForm.querySelector<HTMLInputElement>('.combobox-wrapper[data-cbx="Set"] input')
    : null;
  const setVal = ((narrow?.set || "").trim()) || (setInput ? (setInput.value || "").trim() : "");

  // ALL games (Pokemon included, 2026-07-13) search OUR OWN catalog —
  // one fast Supabase query instead of the slow external Pokemon TCG API.
  // Only full API-ID searches still hit api.pokemontcg.io.
  const gameVal = (narrow?.game || "").trim()
    || (cardForm?.querySelector('[name="Game"]') as HTMLSelectElement | null)?.value
    || "Pokemon";
  if (GAME_TO_CATEGORY[gameVal] && !(gameVal === "Pokemon" && idMatch)) {
    const nar: LookupNarrow | undefined = (narrow?.number || setVal)
      ? { number: narrow?.number, set: setVal || undefined }
      : undefined;
    void catalogCardLookup(gameVal, searchTerm, nar);
    return;
  }

  // Build the API query. We try to be smart about ID-style inputs so users can
  // paste card-ID fragments straight from Quick-add and still get a useful grid.
  //
  // Recognised shapes (whole-string match, before name tokenization):
  //   1. "swsh12tg-TG05"           → set.id:swsh12tg number:TG05   (full API ID)
  //   2. "TG05" / "176" / "176/198"→ number:TG05 / number:176       (number-only)
  //   3. Otherwise → fall back to per-token name+number tokenization
  let tokenQuery = "";
  // (2) Number-only with optional "/total" suffix
  const numOnly = !idMatch && cleaned.match(/^([A-Za-z]*\d+[A-Za-z0-9]*)(?:\/[A-Za-z0-9]+)?$/);

  if (idMatch) {
    tokenQuery = `set.id:${idMatch[1].toLowerCase()} number:${idMatch[2]}`;
  } else if (numOnly) {
    tokenQuery = `number:${numOnly[1]}`;
  } else {
    // (3) Per-token name+number search (original behavior)
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    const queryParts = tokens.map(t => {
      if (/^\d+$/.test(t))       return `number:${t}`;
      if (/^[a-zA-Z]+$/.test(t)) return `name:${t}*`;
      // Mixed alphanumeric (e.g. "TG05") → search by number, not name.
      if (/^[A-Za-z]*\d+[A-Za-z0-9]*$/.test(t)) return `number:${t}`;
      // Keep characters the API can actually match: Latin letters (incl.
      // accents — Flabébé requires the exact é), digits, apostrophe (Cynthia's),
      // hyphen, period, &. Symbols the API can't search (δ, ★, ♀ …) are
      // stripped; a token that ends up empty (a lone "δ") is dropped —
      // "Typhlosion δ" still matches via name:Typhlosion*.
      const safe = t.replace(/\u2019/g, "'").replace(/[^\p{Script=Latin}0-9'&.-]/gu, "");
      return safe ? `name:${safe}*` : "";
    }).filter(Boolean);
    if (!queryParts.length) { toast("Enter at least one search term", "err"); return; }
    tokenQuery = queryParts.join(" ");
  }

  const q = setVal ? `${tokenQuery} set.name:"${setVal.replace(/"/g, "")}"` : tokenQuery;

  // When narrowing by number, request the API's maximum 250 results in one
  // shot - the client-side filter below is the actual narrowing step and
  // pagination over filtered results would be confusing.
  const effectivePageSize = narrow?.number ? 250 : PAGE_SIZE;
  const effectivePage     = narrow?.number ? 1 : page;
  const url = `https://api.pokemontcg.io/v2/cards?q=${encodeURIComponent(q)}&pageSize=${effectivePageSize}&page=${effectivePage}&orderBy=-set.releaseDate`;
  const headers: Record<string, string> = {};
  const apiKey = Settings.get().pokemonTcgApiKey;
  if (apiKey) headers["X-Api-Key"] = apiKey;

  showLoading(searchTerm, page);

  fetch(url, { headers })
    .then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
    .then(j => {
      const rawCards: ApiCard[] = j.data || [];
      const totalCount: number = typeof j.totalCount === "number" ? j.totalCount : rawCards.length;
      // Client-side number narrowing. Matches when card.number equals (or
      // starts with / contains) the user's number, with the "/total" suffix
      // stripped and leading zeros trimmed for tolerance. Example matches:
      //   user "176"      <-> api "176"        : exact
      //   user "245/217"  <-> api "245"        : after slash-strip
      //   user "25"       <-> api "025"        : after leading-zero trim
      //   user "TG05"     <-> api "TG05"       : exact (case-insensitive)
      function normalizeNumber(n: string): string {
        return String(n || "").trim().split("/")[0].replace(/^0+(?=\d)/, "").toLowerCase();
      }
      let cards: ApiCard[] = rawCards;
      let narrowedFrom = 0;
      if (narrow?.number) {
        const want = normalizeNumber(narrow.number);
        if (want) {
          const filtered = rawCards.filter(c => normalizeNumber(c.number) === want);
          if (filtered.length > 0) {
            narrowedFrom = rawCards.length;
            cards = filtered;
          }
          // If zero exact-number matches, keep all results so the user can
          // still pick - we surface the situation in the notice below.
        }
      }
      _quickCards = cards;
      const totalPages = narrow?.number
        ? 1
        : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
      const narrowNotice = narrow?.number
        ? (narrowedFrom > 0
            ? `<br>Narrowed to <b>${cards.length}</b> matching <b>#${escapeHtml(narrow.number)}</b> (from ${narrowedFrom} name match${narrowedFrom === 1 ? "" : "es"}).`
            : `<br><span class="text-amber-300">No card with <b>#${escapeHtml(narrow.number)}</b> in the name results - showing all ${cards.length}.</span>`)
        : "";
      const refineEsc = escapeHtml(searchTerm).replace(/'/g, "&#39;");
      const body = `
        <div class="space-y-3 mb-3">
          <div class="flex flex-wrap gap-2">
            <input type="text" id="quickLookupRefine" class="input flex-1 min-w-[240px]"
                   placeholder='Refine: e.g. "umbreon 161" or "charizard ex"'
                   value="${refineEsc}">
            <button class="btn btn-primary" id="_qclSearch">Search</button>
          </div>
          <div class="text-xs text-[var(--muted)] panel-2 p-2 rounded">
            Each word filters independently. Numbers (<code>161</code>) match card number; words (<code>umbreon</code>) match name.
            Combine: <code>umbreon 161</code>, <code>charizard ex</code>, <code>lugia v alt</code>.${setVal ? ` Filtered to set: <b>${escapeHtml(setVal)}</b>.` : ""}
            <br>${cards.length ? `Showing <b>${cards.length}</b> of <b>${totalCount.toLocaleString()}</b> matches.` : "<b>No matches.</b>"} ${totalPages > 1 ? `Page ${page} of ${totalPages}.` : ""}${narrowNotice}
            ${apiKey ? "" : ` · <span class="text-amber-300">Free API key in Configs → Defaults raises rate limits.</span>`}
          </div>
        </div>
        ${cards.length ? `<div class="grid-cards" id="_qclGrid">${cards.map((c, i) => `
          <div class="panel-2 p-3 clickable" data-qcl-idx="${i}">
            <div class="flex gap-2">
              ${c.images?.small ? `<img src="${c.images.small}" class="w-16 rounded shrink-0">` : ""}
              <div class="text-sm min-w-0">
                <div class="font-semibold truncate">${escapeHtml(c.name)}</div>
                <div class="text-xs text-[var(--muted)] truncate">${escapeHtml(c.set?.name || "")} #${escapeHtml(c.number || "")}</div>
                ${c.tcgplayer?.prices ? `<div class="text-xs mt-1 text-emerald-300">${fmt$((Object.values(c.tcgplayer.prices)[0] || {}).market)}</div>` : ""}
                ${trendButtonHtml(`data-qcl-trend="${i}"`)}
              </div>
            </div>
          </div>`).join("")}</div>` : `
          <div class="text-center text-[var(--muted)] py-6 text-sm">No matches. Try different terms or drop the card number.</div>
        `}
        ${totalPages > 1 ? `
          <div class="flex justify-between items-center pt-3 mt-3 border-t border-[var(--border)] text-sm">
            ${page > 1 ? `<button class="btn btn-ghost" data-qcl-page="${page - 1}">← Previous</button>` : `<span></span>`}
            <span class="text-[var(--muted)]">Page ${page} of ${totalPages}</span>
            ${page < totalPages ? `<button class="btn btn-ghost" data-qcl-page="${page + 1}">Next →</button>` : `<span></span>`}
          </div>` : ""}`;
      renderSubModal(`Pick a match — ${totalCount.toLocaleString()} found`, body, `<button class="btn btn-ghost" id="_qclCancel3">Cancel</button>`);

      document.getElementById("_qclCancel3")?.addEventListener("click", closeQuickLookup);
      document.getElementById("_qclSearch")?.addEventListener("click", () => {
        quickCardLookup(1, (document.getElementById("quickLookupRefine") as HTMLInputElement).value);
      });
      document.getElementById("quickLookupRefine")?.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") {
          e.preventDefault();
          quickCardLookup(1, (document.getElementById("quickLookupRefine") as HTMLInputElement).value);
        }
      });
      document.getElementById("_qclGrid")?.addEventListener("click", (e) => {
        const trendBtn = (e.target as HTMLElement).closest<HTMLElement>("[data-qcl-trend]");
        if (trendBtn) { void toggleApiCardTrend(trendBtn); return; }
        if (isTrendClick(e.target)) return;   // clicks inside an open chart never pick
        const tile = (e.target as HTMLElement).closest<HTMLElement>("[data-qcl-idx]");
        if (!tile) return;
        pickCardFromQuick(Number(tile.dataset.qclIdx));
      });
      document.querySelectorAll<HTMLElement>("[data-qcl-page]").forEach(btn => {
        btn.addEventListener("click", () => {
          const p = Number(btn.getAttribute("data-qcl-page"));
          quickCardLookup(p, (document.getElementById("quickLookupRefine") as HTMLInputElement).value);
        });
      });

      setTimeout(() => {
        const ri = document.getElementById("quickLookupRefine") as HTMLInputElement | null;
        if (ri) { ri.focus(); ri.setSelectionRange(ri.value.length, ri.value.length); }
      }, 50);
    })
    .catch(e => {
      console.error("[quickCardLookup] fetch failed:", e);
      showError(searchTerm, e);
    });
}

export function pickCardFromQuick(i: number): void {
  const c = _quickCards[i];
  if (!c) { closeQuickLookup(); return; }

  const onPick = (window as any)._lookupOnPick;
  if (typeof onPick === "function") {
    try { onPick(c); }
    catch (e) { console.error("[lookup onPick]", e); }
    closeQuickLookup();
    return;
  }

  const form = document.getElementById("cardForm") as HTMLFormElement | null;
  if (form) {
    const setVal = (name: string, value: string) => {
      const el = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (el && value != null) el.value = value;
    };
    setVal("Card Name", c.name || "");
    setVal("Card #", c.number || "");
    const setInput = form.querySelector<HTMLInputElement>('.combobox-wrapper[data-cbx="Set"] input');
    if (setInput && c.set?.name) setInput.value = c.set.name;
    // Image URL — small thumbnail preferred; falls back to large.
    const imgUrl = (c.images && (c.images.small || c.images.large)) || "";
    if (imgUrl) setVal("Image URL", imgUrl);
    const imgInput = form.querySelector<HTMLInputElement>('[name="Image URL"]');
    if (imgInput && imgUrl) imgInput.dispatchEvent(new Event("input", { bubbles: true }));
    const tcg = c.tcgplayer?.prices;
    if (tcg) {
      const p = Object.values(tcg).find(x => x && typeof x.market === "number");
      if (p) {
        const adj = applyFormCondition(form, Number(p.market), "TCGPlayer");
        const fmvField = form.querySelector<HTMLInputElement>('[name="Current FMV"]');
        if (fmvField) fmvField.value = String(adj.unit);
        setVal("Valuation Source", adj.source);
      }
    }

    // Canonical identity (Phase 1c). The API card id is known immediately;
    // the TCGplayer product id is resolved against our own catalog in the
    // background and fills its hidden field when (and only when) exactly one
    // catalog row matches. Saving before it lands just means "resolve later".
    setVal("PokemonTCG ID", c.id || "");
    setVal("TCG Product ID", "");   // stale value from a previous pick must not survive
    setVal("Price Variant", "");    // ditto — a pinned printing belongs to the OLD card
    const gameVal = (form.querySelector<HTMLSelectElement>('[name="Game"]')?.value) || "Pokemon";
    resolveCatalogProduct({ game: gameVal, name: c.name || "", number: c.number || "", set: c.set?.name || "" })
      .then(m => {
        if (!m) return;
        const pidField = form.querySelector<HTMLInputElement>('[name="TCG Product ID"]');
        // Only fill if the form still shows the card we resolved for.
        const nameNow = form.querySelector<HTMLInputElement>('[name="Card Name"]')?.value || "";
        if (pidField && nameNow === (c.name || "")) {
          pidField.value = String(m.productId);
          void syncVariantSelect(form, m.productId, "");
        }
      });

    markModalDirty();
    closeQuickLookup();
    toast(`Filled: ${c.name}${c.set?.name ? " · " + c.set.name : ""}`);
    return;
  }

  closeQuickLookup();
  toast("Pick recorded — opening Card form is a chunk-5c follow-up", "ok");
}

/** Pokemon API grid trend: API cards carry no TCGplayer product id, so resolve
 *  it on demand (name+number+set -> unique catalog match) and cache it on the
 *  button. No unique match -> explain rather than chart the wrong card. */
async function toggleApiCardTrend(btn: HTMLElement): Promise<void> {
  const tile = btn.closest<HTMLElement>("[data-qcl-idx]");
  const c = _quickCards[Number(btn.dataset.qclTrend)];
  if (!tile || !c) return;

  let pid = Number(btn.dataset.pid || 0);
  if (!pid) {
    btn.setAttribute("disabled", "disabled");
    try {
      const match = await resolveCatalogProduct({
        game: "Pokemon",
        name: c.name || "",
        number: c.number || "",
        set: c.set?.name || "",
      });
      pid = match?.productId || 0;
    } finally {
      btn.removeAttribute("disabled");
    }
    if (!pid) { toast("No unique catalog match for this card - no trend available", "err"); return; }
    btn.dataset.pid = String(pid);
  }
  await toggleCatalogTrend(tile, "card", pid);
}

/* ============================================================
   Catalog-backed lookup grid — EVERY game (Pokemon included).
   Same modal UX as the API grid; data comes from catalog_products
   (synced nightly from TCGCSV), prices from catalog_current_prices.
   ============================================================ */

let _catalogCards: CatalogSearchRow[] = [];
let _catalogGame = "";

async function catalogCardLookup(game: string, searchTerm: string, narrow?: LookupNarrow): Promise<void> {
  _catalogGame = game;
  showLoading(searchTerm, 1);
  const q = narrow?.number ? `${searchTerm} ${narrow.number}` : searchTerm;
  const setFilter = (narrow?.set || "").trim();
  let rows = await searchCatalogCards(game, q, 60, setFilter || undefined);
  // Set filter too strict (typo, renamed set…) → show everything rather
  // than nothing, and say so.
  let setMissed = false;
  if (!rows.length && setFilter) {
    rows = await searchCatalogCards(game, q, 60);
    setMissed = true;
  }
  _catalogCards = rows;

  const refineEsc = escapeHtml(searchTerm).replace(/'/g, "&#39;");
  const body = `
    <div class="space-y-3 mb-3">
      <div class="flex flex-wrap gap-2">
        <input type="text" id="quickLookupRefine" class="input flex-1 min-w-[240px]"
               placeholder='Refine: e.g. "lightning bolt" or "sol ring 126"'
               value="${refineEsc}">
        <button class="btn btn-primary" id="_qclSearch">Search</button>
      </div>
      <div class="text-xs text-[var(--muted)] panel-2 p-2 rounded">
        Searching the ${escapeHtml(game)} catalog (synced nightly from TCGplayer). Words match the name; numbers match the card number.${setFilter && !setMissed ? ` Filtered to set: <b>${escapeHtml(setFilter)}</b>.` : ""}
        <br>${rows.length ? `Showing <b>${rows.length}</b> match${rows.length === 1 ? "" : "es"}, newest sets first.` : "<b>No matches.</b> Try fewer words."}${setMissed ? ` <span class="text-amber-300">Nothing in set <b>${escapeHtml(setFilter)}</b> — showing all sets.</span>` : ""}
      </div>
    </div>
    ${rows.length ? `<div class="grid-cards" id="_qclGrid">${rows.map((c, i) => `
      <div class="panel-2 p-3 clickable" data-qcl-idx="${i}">
        <div class="flex gap-2">
          ${c.imageUrl ? `<img src="${escapeHtml(c.imageUrl)}" class="w-16 rounded shrink-0" loading="lazy">` : ""}
          <div class="text-sm min-w-0">
            <div class="font-semibold truncate">${escapeHtml(cleanCardName(c.name, c.cardNumber))}</div>
            <div class="text-xs text-[var(--muted)] truncate">${escapeHtml(cleanGroupName(c.groupName))}${c.cardNumber ? " #" + escapeHtml(c.cardNumber) : ""}</div>
            ${c.variants.length > 1 ? `
              <select class="input !text-xs !py-1 !px-1 mt-1 w-full" data-qcl-var="${i}"
                      title="Printing / edition used for pricing. Saved with the lot so nightly repricing uses THIS tier.">
                ${c.variants.map(v => `<option value="${escapeHtml(v.variant)}">${escapeHtml(v.variant)} — ${fmt$(v.unit)}</option>`).join("")}
              </select>`
            : c.unit != null ? `<div class="text-xs mt-1 text-emerald-300">${fmt$(c.unit)}${c.variant ? ` <span class="text-[var(--muted)]">${escapeHtml(c.variant)}</span>` : ""}</div>` : ""}
            ${trendButtonHtml(`data-qcl-trend="${i}"`)}
          </div>
        </div>
      </div>`).join("")}</div>` : `
      <div class="text-center text-[var(--muted)] py-6 text-sm">No matches in the ${escapeHtml(game)} catalog. Check spelling or drop the number.</div>
    `}`;
  renderSubModal(`Pick a match — ${escapeHtml(game)}`, body, `<button class="btn btn-ghost" id="_qclCancel3">Cancel</button>`);

  document.getElementById("_qclCancel3")?.addEventListener("click", closeQuickLookup);
  const runRefine = () => {
    const v = (document.getElementById("quickLookupRefine") as HTMLInputElement).value;
    // Keep the set filter across refines; the number is deliberately dropped
    // (the refine text supersedes it, same as the API grid).
    void catalogCardLookup(game, v, setFilter ? { set: setFilter } : undefined);
  };
  document.getElementById("_qclSearch")?.addEventListener("click", runRefine);
  document.getElementById("quickLookupRefine")?.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); runRefine(); }
  });
  document.getElementById("_qclGrid")?.addEventListener("click", (e) => {
    const trendBtn = (e.target as HTMLElement).closest<HTMLElement>("[data-qcl-trend]");
    if (trendBtn) {
      const c = _catalogCards[Number(trendBtn.dataset.qclTrend)];
      const tile = trendBtn.closest<HTMLElement>("[data-qcl-idx]");
      if (c && tile) void toggleCatalogTrend(tile, "card", c.productId);
      return;
    }
    if (isTrendClick(e.target)) return;   // clicks inside an open chart never pick
    // Choosing a printing must not also pick the card — the user is still
    // deciding which edition this lot is.
    if ((e.target as HTMLElement).closest("[data-qcl-var]")) return;
    const tile = (e.target as HTMLElement).closest<HTMLElement>("[data-qcl-idx]");
    if (!tile) return;
    pickCatalogCard(Number(tile.dataset.qclIdx));
  });
  setTimeout(() => {
    const ri = document.getElementById("quickLookupRefine") as HTMLInputElement | null;
    if (ri) { ri.focus(); ri.setSelectionRange(ri.value.length, ri.value.length); }
  }, 50);
}

function pickCatalogCard(i: number): void {
  const c = _catalogCards[i];
  if (!c) { closeQuickLookup(); return; }
  const displayName = cleanCardName(c.name, c.cardNumber);

  // Which printing did the user settle on? The per-tile <select> is the only
  // place this can be answered — TCGplayer prices 1st Edition and Unlimited
  // separately and they are routinely 3-6x apart, so we record the choice on
  // the lot (price_variant) instead of letting the default ladder guess.
  const sel = document.querySelector<HTMLSelectElement>(`[data-qcl-var="${i}"]`);
  const chosenVariant = (sel?.value || c.variant || "").trim();
  const chosen = c.variants.find(v => v.variant === chosenVariant);
  const chosenUnit = chosen ? chosen.unit : c.unit;
  // Only pin the variant when there was a real choice to make; a single-tier
  // product left unpinned keeps following the ladder if TCGplayer adds tiers.
  const pinVariant = c.variants.length > 1 ? chosenVariant : "";

  // Inline pickers (purchase/trade item rows) expect the API-card shape.
  const onPick = (window as any)._lookupOnPick;
  if (typeof onPick === "function") {
    try {
      onPick({
        id: "",
        game: _catalogGame || undefined,
        name: displayName,
        number: c.cardNumber || "",
        set: { name: normalizeSetName(c.groupName) },
        images: c.imageUrl ? { small: c.imageUrl } : undefined,
        tcgplayer: chosenUnit != null ? { prices: { normal: { market: chosenUnit } } } : undefined,
      });
    } catch (e) { console.error("[catalog onPick]", e); }
    closeQuickLookup();
    return;
  }

  const form = document.getElementById("cardForm") as HTMLFormElement | null;
  if (form) {
    const setVal = (name: string, value: string) => {
      const el = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (el && value != null) el.value = value;
    };
    setVal("Card Name", displayName);
    setVal("Card #", c.cardNumber || "");
    const setInput = form.querySelector<HTMLInputElement>('.combobox-wrapper[data-cbx="Set"] input');
    if (setInput) setInput.value = normalizeSetName(c.groupName);
    if (c.imageUrl) {
      setVal("Image URL", c.imageUrl);
      form.querySelector<HTMLInputElement>('[name="Image URL"]')?.dispatchEvent(new Event("input", { bubbles: true }));
    }
    if (chosenUnit != null) {
      const adj = applyFormCondition(
        form, chosenUnit,
        chosenVariant ? `TCGplayer Market (${chosenVariant})` : "TCGPlayer",
      );
      const fmvField = form.querySelector<HTMLInputElement>('[name="Current FMV"]');
      if (fmvField) fmvField.value = String(adj.unit);
      setVal("Valuation Source", adj.source);
    }
    // Canonical identity is known DIRECTLY here — no background resolve needed.
    setVal("TCG Product ID", String(c.productId));
    setVal("PokemonTCG ID", "");
    setVal("Price Variant", pinVariant);
    syncVariantSelect(form, c.productId, pinVariant);
    markModalDirty();
    closeQuickLookup();
    toast(`Filled: ${displayName}${c.groupName ? " · " + cleanGroupName(c.groupName) : ""}${pinVariant ? " · " + pinVariant : ""}`);
    return;
  }
  closeQuickLookup();
}

// Inline-handler bridges
(window as any).quickCardLookup   = quickCardLookup;
(window as any).pickCardFromQuick = pickCardFromQuick;
(window as any).closeQuickLookup  = closeQuickLookup;
